import { z } from "zod";
import { isAumSystemAttribute } from "./aum";
import type { CustomAttributeDto } from "./type";

/**
 * Cap on a user-typed AUM. IEEE-754 doubles keep monetary math reliable up to ~$10T (see
 * `FLOAT_SIGNIFICANT_DIGITS` in `$root/utils/monetary-math`) — the same bound as the portfolio lab's
 * `MAX_LAB_AUM` and the quantity editor's `MAX_QUANTITY_AUM`, defined here so `./AumField` stays
 * self-contained.
 */
export const MAX_AUM = 1e13;

type AumCapValues = Array<{ attributeUuid?: string | null; numberValue?: number | null } | undefined> | undefined;

/**
 * Zod `superRefine` for a form's `customAttributes`: flags the AUM entry's `numberValue` when it
 * exceeds {@link MAX_AUM}. Matches nothing while the tenant's attributes are unknown or have no AUM.
 */
export function refineAumCap(
	availableAttributes: CustomAttributeDto[] | undefined,
): (values: AumCapValues, ctx: z.RefinementCtx) => void {
	const aumAttributeUuid = availableAttributes?.find(isAumSystemAttribute)?.attributeUuid;
	return (values, ctx) => {
		if (aumAttributeUuid == null) {
			return;
		}
		values?.forEach((value, index) => {
			if (value?.attributeUuid === aumAttributeUuid && value.numberValue != null && value.numberValue > MAX_AUM) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [index, "numberValue"],
					message: `The portfolio AUM cannot exceed ${MAX_AUM.toLocaleString("en-US")}`,
				});
			}
		});
	};
}
